/**
 * OverheadCosts
 *
 * Monthly overhead cost entry for the substrate operation.
 * Follows the same pattern as FixedCostsPage.
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, Trash2, Loader2, Receipt, Copy } from 'lucide-react'
import { OVERHEAD_CATEGORIES } from '@/hooks/useSubstrateLogs'

const CATEGORY_LABELS = {
  container_rental: 'Container Rental',
  electricity: 'Electricity',
  transport: 'Transport',
  other: 'Other'
}

export default function OverheadCosts({ hook, periodStart }) {
  const { overheads, addOverhead, deleteOverhead, isLoading } = hook

  const [newItem, setNewItem] = useState({
    category: 'container_rental',
    description: '',
    amount: ''
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)

  const allTotal = useMemo(() => {
    return overheads.reduce((sum, o) => sum + Number(o.amount), 0)
  }, [overheads])

  const handleAdd = async () => {
    if (!newItem.amount || parseFloat(newItem.amount) <= 0) return
    const ps = newItem.periodStart || periodStart
    const [y, m] = ps.split('-')
    const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate()
    const pe = `${y}-${m}-${String(lastDay).padStart(2, '0')}`
    setSaving(true)
    try {
      await addOverhead({
        period_start: ps,
        period_end: pe,
        category: newItem.category,
        description: newItem.description || CATEGORY_LABELS[newItem.category],
        amount: parseFloat(newItem.amount)
      })
      setNewItem({ category: 'container_rental', description: '', amount: '' })
    } catch (err) {
      console.error('Failed to add overhead:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(id)
    try {
      await deleteOverhead(id)
    } catch (err) {
      console.error('Failed to delete overhead:', err)
    } finally {
      setDeleting(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Overhead Costs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add New Row */}
          <div className="flex items-end gap-3">
            <div className="w-40">
              <Label className="text-xs">Category</Label>
              <Select value={newItem.category} onValueChange={(val) => setNewItem(prev => ({ ...prev, category: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OVERHEAD_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs">Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
              />
            </div>
            <div className="w-32">
              <Label className="text-xs">Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newItem.amount}
                onChange={(e) => setNewItem(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <Button onClick={handleAdd} disabled={saving || !newItem.amount} size="sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          {/* All Items */}
          {overheads.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No overhead costs recorded. Add items above.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overheads.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-muted-foreground">
                      {new Date(item.period_start + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{CATEGORY_LABELS[item.category] || item.category}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{item.description}</TableCell>
                    <TableCell className="text-right font-medium">£{Number(item.amount).toFixed(2)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleting === item.id}
                      >
                        {deleting === item.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <TableRow className="font-medium border-t">
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell className="text-right">£{allTotal.toFixed(2)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
